import { RolePermissionsResponse, RoleResources } from "Models/resources";
import { User } from "Models/user";

import { ResourceGroup, ResourceName, ResourceSubGroup } from "./enums";
import { getResourceByNameGroup } from "./getResourceByNameGroup";
import { ResourceObjectTypeEnum } from "./enums/accessRule";
import { isMoving } from "./validation";

const DEFAULT_PERMISSIONS = {
  view: false,
  edit: false,
  add: false,
  delete: false,
} as RoleResources;

interface PermissionProps {
  subGroup?: string;
  resourceName?: string;
  currentUser?: User;
}

const operationsPermissions = ({ subGroup, resourceName, currentUser }: PermissionProps) =>
  getResourceByNameGroup(
    ResourceGroup.OPERATIONS,
    subGroup || ResourceSubGroup.OPERATIONS,
    resourceName || ResourceName.ORDERS,
    currentUser?.resources
  )?.roles_resources || DEFAULT_PERMISSIONS;

const warehousingPermissions = ({ subGroup, resourceName, currentUser }: PermissionProps) =>
  getResourceByNameGroup(
    ResourceGroup.WAREHOUSING,
    subGroup || ResourceSubGroup.WAREHOUSING,
    resourceName || ResourceName.TRANSSHIPMENT,
    currentUser?.resources
  )?.roles_resources || DEFAULT_PERMISSIONS;

const salesPermissions = ({ subGroup, resourceName, currentUser }: PermissionProps) =>
  getResourceByNameGroup(
    ResourceGroup.SALES,
    subGroup || ResourceSubGroup.SALES,
    resourceName || ResourceName.QUOTE,
    currentUser?.resources
  )?.roles_resources || DEFAULT_PERMISSIONS;

const managementPermissions = ({ subGroup, resourceName, currentUser }: PermissionProps) =>
  getResourceByNameGroup(
    ResourceGroup.MANAGEMENT,
    subGroup || ResourceSubGroup.MANAGEMENT,
    resourceName || ResourceName.MANAGEMENT,
    currentUser?.resources
  )?.roles_resources || DEFAULT_PERMISSIONS;

const checkResourceCan = (
  permissions: RolePermissionsResponse[],
  resourceName: string,
  type: "edit" | "view",
  resourceSubGroup?: string,
  resourceGroup?: string
) => {
  const resource = permissions
    ?.filter((permission) => permission?.objectType === ResourceObjectTypeEnum.RESOURCE)
    ?.find((permission) => {
      if (resourceGroup) {
        if (resourceSubGroup) {
          return (
            permission?.objectDetails?.group?.toLowerCase() === resourceGroup?.toLowerCase() &&
            permission?.objectDetails?.subGroup?.toLowerCase() ===
              resourceSubGroup?.toLowerCase() &&
            permission?.objectDetails?.name?.toLowerCase() === resourceName?.toLowerCase()
          );
        }

        return (
          permission?.objectDetails?.group?.toLowerCase() === resourceGroup?.toLowerCase() &&
          permission?.objectDetails?.name?.toLowerCase() === resourceName?.toLowerCase()
        );
      }

      if (resourceSubGroup) {
        return (
          permission?.objectDetails?.subGroup?.toLowerCase() === resourceSubGroup?.toLowerCase() &&
          permission?.objectDetails?.name?.toLowerCase() === resourceName?.toLowerCase()
        );
      }

      return permission?.objectDetails?.name?.toLowerCase() === resourceName?.toLowerCase();
    });

  return type === "edit" ? resource?.canEdit === 1 : resource?.canView === 1;
};

const hasPermission = (permissions: RolePermissionsResponse[], permissionCode: string): boolean => {
  return permissions
    ?.filter((permission) => permission?.objectType === ResourceObjectTypeEnum.FUNCTION)
    ?.some((permission) =>
      permission?.objectDetails?.code === permissionCode ? permission?.canEdit : false
    );
};

const isShowAction = (currentUser: User | undefined, isHavePermission: boolean): boolean => {
  return (isMoving(currentUser?.company) && isHavePermission) || !isMoving(currentUser?.company);
};

const FN_NAMES = {
  // Reports
  FUNCTION_REPORTS_GENERAL_VIEW: "FUNCTION_REPORTS_GENERAL_VIEW",
  FUNCTION_REPORTS_GENERAL_FAVORITES: "FUNCTION_REPORTS_GENERAL_FAVORITES",
  FUNCTION_REPORTS_GENERAL_EMAIL_SUBSCRIPTIONS: "FUNCTION_REPORTS_GENERAL_EMAIL_SUBSCRIPTIONS",
  FUNCTION_REPORTS_GENERAL_ACTIVITIES: "FUNCTION_REPORTS_GENERAL_ACTIVITIES",
  FUNCTION_REPORTS_GENERAL_DEACTIVATE: "FUNCTION_REPORTS_GENERAL_DEACTIVATE",
  FUNCTION_REPORTS_WAREHOUSES_MOVEMENTS: "FUNCTION_REPORTS_WAREHOUSES_MOVEMENTS",
  FUNCTION_REPORTS_WAREHOUSES_LOCATION_STATUS: "FUNCTION_REPORTS_WAREHOUSES_LOCATION_STATUS",
  FUNCTION_REPORTS_ORDER_INVENTORY_CONDITION: "FUNCTION_REPORTS_ORDER_INVENTORY_CONDITION",
  FUNCTION_REPORTS_ORDER_HIGH_RISK_HIGH_VALUE: "FUNCTION_REPORTS_ORDER_HIGH_RISK_HIGH_VALUE",
  FUNCTION_REPORTS_ORDER_LOAD_TRANSSHIPMENT: "FUNCTION_REPORTS_ORDER_LOAD_TRANSSHIPMENT",
  FUNCTION_REPORTS_ORDER_PRO_GEAR: "FUNCTION_REPORTS_ORDER_PRO_GEAR",
  FUNCTION_REPORTS_ORDER_MECHANICAL_ELECTRICAL_CHECKLIST:
    "FUNCTION_REPORTS_ORDER_MECHANICAL_ELECTRICAL_CHECKLIST",
  FUNCTION_REPORTS_ORDER_OUTTURN: "FUNCTION_REPORTS_ORDER_OUTTURN",
  FUNCTION_REPORTS_ORDER_PROPERTY_DAMAGE: "FUNCTION_REPORTS_ORDER_PROPERTY_DAMAGE",
  FUNCTION_REPORTS_BUSINESS_VIEW: "FUNCTION_REPORTS_BUSINESS_VIEW",
  FUNCTION_REPORTS_ORDERS_VIEW: "FUNCTION_REPORTS_ORDERS_VIEW",
  FUNCTION_REPORTS_OPERATIONS_VIEW: "FUNCTION_REPORTS_OPERATIONS_VIEW",
  FUNCTION_REPORTS_FAVORITES_EDIT: "FUNCTION_REPORTS_FAVORITES_EDIT",
  FUNCTION_REPORTS_FAVORITES_VIEW: "FUNCTION_REPORTS_FAVORITES_VIEW",

  // Sales
  FUNCTION_SALES_QUOTE_CANCEL_QUOTE: "FUNCTION_SALES_QUOTE_CANCEL_QUOTE",
  FUNCTION_SALES_QUOTE_EDIT_ESTIMATE: "FUNCTION_SALES_QUOTE_EDIT_ESTIMATE",
  FUNCTION_SALES_QUOTE_COMMUNICATION: "FUNCTION_SALES_QUOTE_COMMUNICATION",
  FUNCTION_SALES_QUOTE_EDIT: "FUNCTION_SALES_QUOTE_EDIT",
  FUNCTION_SALES_QUOTE_VIEW_ALL: "FUNCTION_SALES_QUOTE_VIEW_ALL",
  FUNCTION_SALES_QUOTE_VIDEO_SURVEY: "FUNCTION_SALES_QUOTE_VIDEO_SURVEY",
  FUNCTION_SALES_QUOTE_DECLINE: "FUNCTION_SALES_QUOTE_DECLINE",
  FUNCTION_SALES_QUOTE_APPROVE: "FUNCTION_SALES_QUOTE_APPROVE",
  FUNCTION_SALES_QUOTE_ADD_NEW: "FUNCTION_SALES_QUOTE_ADD_NEW",
  FUNCTION_SALES_QUOTE_VIEW_OWN: "FUNCTION_SALES_QUOTE_VIEW_OWN",

  // Invoices
  FUNCTION_INVOICES_GENERAL_VIEW: "FUNCTION_INVOICES_GENERAL_VIEW",
  FUNCTION_INVOICES_GENERAL_EDIT: "FUNCTION_INVOICES_GENERAL_EDIT",
  FUNCTION_INVOICES_GENERAL_ADD_NEW: "FUNCTION_INVOICES_GENERAL_ADD_NEW",
  FUNCTION_INVOICES_GENERAL_DELETE: "FUNCTION_INVOICES_GENERAL_DELETE",

  // Network
  FUNCTION_NETWORK_ACCOUNTS_VIEW: "FUNCTION_NETWORK_ACCOUNTS_VIEW",
  FUNCTION_NETWORK_ACCOUNTS_EDIT: "FUNCTION_NETWORK_ACCOUNTS_EDIT",
  FUNCTION_NETWORK_ACCOUNTS_ADD_NEW: "FUNCTION_NETWORK_ACCOUNTS_ADD_NEW",
  FUNCTION_NETWORK_ACCOUNTS_CHANGE_STATUS: "FUNCTION_NETWORK_ACCOUNTS_CHANGE_STATUS",
  FUNCTION_NETWORK_AGENTS_ADD_NEW: "FUNCTION_NETWORK_AGENTS_ADD_NEW",
  FUNCTION_NETWORK_AGENTS_CHANGE_STATUS: "FUNCTION_NETWORK_AGENTS_CHANGE_STATUS",
  FUNCTION_NETWORK_AGENTS_EDIT: "FUNCTION_NETWORK_AGENTS_EDIT",
  FUNCTION_NETWORK_AGENTS_VIEW: "FUNCTION_NETWORK_AGENTS_VIEW",
  FUNCTION_NETWORK_CUSTOMERS_ADD_NEW: "FUNCTION_NETWORK_CUSTOMERS_ADD_NEW",
  FUNCTION_NETWORK_CUSTOMERS_CHANGE_STATUS: "FUNCTION_NETWORK_CUSTOMERS_CHANGE_STATUS",
  FUNCTION_NETWORK_CUSTOMERS_EDIT: "FUNCTION_NETWORK_CUSTOMERS_EDIT",
  FUNCTION_NETWORK_CUSTOMERS_VIEW: "FUNCTION_NETWORK_CUSTOMERS_VIEW",
  FUNCTION_NETWORK_SUPPLIERS_ADD_NEW: "FUNCTION_NETWORK_SUPPLIERS_ADD_NEW",
  FUNCTION_NETWORK_SUPPLIERS_EDIT: "FUNCTION_NETWORK_SUPPLIERS_EDIT",
  FUNCTION_NETWORK_SUPPLIERS_VIEW: "FUNCTION_NETWORK_SUPPLIERS_VIEW",

  // Operations
  FUNCTION_OPERATIONS_DISPATCH_EDIT: "FUNCTION_OPERATIONS_DISPATCH_EDIT",
  FUNCTION_OPERATIONS_DISPATCH_SEARCH: "FUNCTION_OPERATIONS_DISPATCH_SEARCH",
  FUNCTION_OPERATIONS_DISPATCH_SETTINGS: "FUNCTION_OPERATIONS_DISPATCH_SETTINGS",
  FUNCTION_OPERATIONS_DISPATCH_FILTER: "FUNCTION_OPERATIONS_DISPATCH_FILTER",
  FUNCTION_OPERATIONS_DISPATCH_PRINT_REPORT: "FUNCTION_OPERATIONS_DISPATCH_PRINT_REPORT",
  FUNCTION_OPERATIONS_DISPATCH_VIEW_MONTH: "FUNCTION_OPERATIONS_DISPATCH_VIEW_MONTH",
  FUNCTION_OPERATIONS_DISPATCH_VIEW_WEEK: "FUNCTION_OPERATIONS_DISPATCH_VIEW_WEEK",
  FUNCTION_OPERATIONS_DISPATCH_VIEW_DAY: "FUNCTION_OPERATIONS_DISPATCH_VIEW_DAY",
  FUNCTION_OPERATIONS_DISPATCH_VIEW_LIST: "FUNCTION_OPERATIONS_DISPATCH_VIEW_LIST",
  FUNCTION_OPERATIONS_DISPATCH_NEW_TEAM: "FUNCTION_OPERATIONS_DISPATCH_NEW_TEAM",
  FUNCTION_OPERATIONS_DISPATCH_NEW_TASK: "FUNCTION_OPERATIONS_DISPATCH_NEW_TASK",
  FUNCTION_OPERATIONS_DISPATCH_NEW_PLACEHOLDER_TASK:
    "FUNCTION_OPERATIONS_DISPATCH_NEW_PLACEHOLDER_TASK",
  FUNCTION_OPERATIONS_DISPATCH_NEW_COMMENT: "FUNCTION_OPERATIONS_DISPATCH_NEW_COMMENT",
  FUNCTION_OPERATIONS_DISPATCH_TASKS: "FUNCTION_OPERATIONS_DISPATCH_TASKS",
  FUNCTION_OPERATIONS_DISPATCH_TEAMS: "FUNCTION_OPERATIONS_DISPATCH_TEAMS",
  FUNCTION_OPERATIONS_DISPATCH_MEMBERS: "FUNCTION_OPERATIONS_DISPATCH_MEMBERS",
  FUNCTION_OPERATIONS_DISPATCH_EQUIPMENTS: "FUNCTION_OPERATIONS_DISPATCH_EQUIPMENTS",
  FUNCTION_OPERATIONS_DISPATCH_STORAGE_BOXES: "FUNCTION_OPERATIONS_DISPATCH_STORAGE_BOXES",
  FUNCTION_OPERATIONS_ORDERS_VIEW: "FUNCTION_OPERATIONS_ORDERS_VIEW",
  FUNCTION_OPERATIONS_ORDERS_VIDEO_SURVEY: "FUNCTION_OPERATIONS_ORDERS_VIDEO_SURVEY",
  FUNCTION_OPERATIONS_ORDERS_DECLINE: "FUNCTION_OPERATIONS_ORDERS_DECLINE",
  FUNCTION_OPERATIONS_ORDERS_CANCEL_ORDER: "FUNCTION_OPERATIONS_ORDERS_CANCEL_ORDER",
  FUNCTION_OPERATIONS_ORDERS_DELIVERY: "FUNCTION_OPERATIONS_ORDERS_DELIVERY",
  FUNCTION_OPERATIONS_ORDERS_PRINT_REPORT: "FUNCTION_OPERATIONS_ORDERS_PRINT_REPORT",
  FUNCTION_OPERATIONS_ORDERS_COMMUNICATION: "FUNCTION_OPERATIONS_ORDERS_COMMUNICATION",
  FUNCTION_OPERATIONS_ORDERS_EDIT: "FUNCTION_OPERATIONS_ORDERS_EDIT",
  FUNCTION_OPERATIONS_ORDERS_ADD_NEW: "FUNCTION_OPERATIONS_ORDERS_ADD_NEW",
  FUNCTION_OPERATIONS_ORDERS_APPROVE: "FUNCTION_OPERATIONS_ORDERS_APPROVE",
  FUNCTION_OPERATIONS_SHIPMENTS_EDIT: "FUNCTION_OPERATIONS_SHIPMENTS_EDIT",
  FUNCTION_OPERATIONS_SHIPMENTS_DELETE: "FUNCTION_OPERATIONS_SHIPMENTS_DELETE",
  FUNCTION_OPERATIONS_SHIPMENTS_PRINT_REPORT: "FUNCTION_OPERATIONS_SHIPMENTS_PRINT_REPORT",
  FUNCTION_OPERATIONS_SHIPMENTS_COMMUNICATION: "FUNCTION_OPERATIONS_SHIPMENTS_COMMUNICATION",
  FUNCTION_OPERATIONS_SHIPMENTS_VIEW_ROAD: "FUNCTION_OPERATIONS_SHIPMENTS_VIEW_ROAD",
  FUNCTION_OPERATIONS_SHIPMENTS_VIEW_AIR: "FUNCTION_OPERATIONS_SHIPMENTS_VIEW_AIR",
  FUNCTION_OPERATIONS_SHIPMENTS_VIEW_SEA: "FUNCTION_OPERATIONS_SHIPMENTS_VIEW_SEA",
  FUNCTION_OPERATIONS_SHIPMENTS_VIEW_RAIL: "FUNCTION_OPERATIONS_SHIPMENTS_VIEW_RAIL",
  FUNCTION_OPERATIONS_SHIPMENTS_DRAFT: "FUNCTION_OPERATIONS_SHIPMENTS_DRAFT",
  FUNCTION_OPERATIONS_SHIPMENTS_PUBLISH: "FUNCTION_OPERATIONS_SHIPMENTS_PUBLISH",
  FUNCTION_OPERATIONS_BOOKING_ADD_NEW: "FUNCTION_OPERATIONS_BOOKING_ADD_NEW",
  FUNCTION_OPERATIONS_BOOKING_EDIT: "FUNCTION_OPERATIONS_BOOKING_EDIT",
  FUNCTION_OPERATIONS_BOOKING_VIEW_ALL: "FUNCTION_OPERATIONS_BOOKING_VIEW_ALL",
  FUNCTION_OPERATIONS_BOOKING_VIEW_OWN: "FUNCTION_OPERATIONS_BOOKING_VIEW_OWN",
  FUNCTION_OPERATIONS_BOOKING_EXPORT: "FUNCTION_OPERATIONS_BOOKING_EXPORT",

  // Warehouse
  FUNCTION_WAREHOUSING_MOVEMENT_ADD_NEW: "FUNCTION_WAREHOUSING_MOVEMENT_ADD_NEW",
  FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_1:
    "FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_1",
  FUNCTION_WAREHOUSING_MOVEMENT_PRINT: "FUNCTION_WAREHOUSING_MOVEMENT_PRINT",
  FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_2:
    "FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_2",
  FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_3:
    "FUNCTION_WAREHOUSING_MOVEMENTS_VIEW_WAREHOUSE_3",
  FUNCTION_WAREHOUSING_STORAGE_VIEW: "FUNCTION_WAREHOUSING_STORAGE_VIEW",
  FUNCTION_WAREHOUSING_STORAGE_UNLOAD: "FUNCTION_WAREHOUSING_STORAGE_UNLOAD",
  FUNCTION_WAREHOUSING_STORAGE_CHANGE_LOCATION: "FUNCTION_WAREHOUSING_STORAGE_CHANGE_LOCATION",
  FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_3: "FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_3",
  FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_2: "FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_2",
  FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_1: "FUNCTION_WAREHOUSING_STORAGE_VIEW_WAREHOUSE_1",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_ADD_NEW: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_ADD_NEW",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_EDIT: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_EDIT",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_DELETE: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_DELETE",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_CANCEL: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_CANCEL",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_PUBLISH: "FUNCTION_WAREHOUSING_TRANSSHIPMENT_PUBLISH",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_CHANGE_ASSIGNEE:
    "FUNCTION_WAREHOUSING_TRANSSHIPMENT_CHANGE_ASSIGNEE",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_1:
    "FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_1",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_2:
    "FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_2",
  FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_3:
    "FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW_WAREHOUSE_3",

  // Settings
  FUNCTION_SETTINGS_COMPANY_GENERAL_VIEW: "FUNCTION_SETTINGS_COMPANY_GENERAL_VIEW",
  FUNCTION_SETTINGS_COMPANY_GENERAL_EDIT: "FUNCTION_SETTINGS_COMPANY_GENERAL_EDIT",
  FUNCTION_CONFIGURATIONS_GENERAL_VIEW: "FUNCTION_CONFIGURATIONS_GENERAL_VIEW",
  FUNCTION_CONFIGURATIONS_BRANCHES_VIEW: "FUNCTION_CONFIGURATIONS_BRANCHES_VIEW",
  FUNCTION_CONFIGURATIONS_AUTOMATION_VIEW: "FUNCTION_CONFIGURATIONS_AUTOMATION_VIEW",
  FUNCTION_CONFIGURATIONS_WAREHOUSING_VIEW: "FUNCTION_CONFIGURATIONS_WAREHOUSING_VIEW",
  FUNCTION_CONFIGURATIONS_UPLOAD_CENTRE_VIEW: "FUNCTION_CONFIGURATIONS_UPLOAD_CENTRE_VIEW",
  FUNCTION_CONFIGURATIONS_GENERAL_EDIT: "FUNCTION_CONFIGURATIONS_GENERAL_EDIT",
  FUNCTION_CONFIGURATIONS_GENERAL_ADD_NEW: "FUNCTION_CONFIGURATIONS_GENERAL_ADD_NEW",
  FUNCTION_CONFIGURATIONS_BRANCHES_EDIT: "FUNCTION_CONFIGURATIONS_BRANCHES_EDIT",
  FUNCTION_CONFIGURATIONS_AUTOMATION_EDIT: "FUNCTION_CONFIGURATIONS_AUTOMATION_EDIT",
  FUNCTION_CONFIGURATIONS_WAREHOUSING_EDIT: "FUNCTION_CONFIGURATIONS_WAREHOUSING_EDIT",
  FUNCTION_CONFIGURATIONS_UPLOAD_CENTRE_EDIT: "FUNCTION_CONFIGURATIONS_UPLOAD_CENTRE_EDIT",
  FUNCTION_CONFIGURATIONS_BRANCHES_ADD_NEW: "FUNCTION_CONFIGURATIONS_BRANCHES_ADD_NEW",
  FUNCTION_CONFIGURATIONS_WAREHOUSING_ADD_NEW: "FUNCTION_CONFIGURATIONS_WAREHOUSING_ADD_NEW",
  FUNCTION_CONFIGURATIONS_AUTOMATE_EDIT: "FUNCTION_CONFIGURATIONS_AUTOMATE_EDIT",
  FUNCTION_CONFIGURATIONS_AUTOMATE_VIEW: "FUNCTION_CONFIGURATIONS_AUTOMATE_VIEW",
  FUNCTION_CONFIGURATIONS_HS_COMMODITY_CODE_EDIT: "FUNCTION_CONFIGURATIONS_HS_COMMODITY_CODE_EDIT",
  FUNCTION_CONFIGURATIONS_HS_COMMODITY_CODE_VIEW: "FUNCTION_CONFIGURATIONS_HS_COMMODITY_CODE_VIEW",
  FUNCTION_CONFIGURATIONS_PORTS_EDIT: "FUNCTION_CONFIGURATIONS_PORTS_EDIT",
  FUNCTION_CONFIGURATIONS_PORTS_VIEW: "FUNCTION_CONFIGURATIONS_PORTS_VIEW",
  FUNCTION_CONFIGURATIONS_OFFICES_VIEW: "FUNCTION_CONFIGURATIONS_OFFICES_VIEW",
  FUNCTION_CONFIGURATIONS_OFFICES_EDIT: "FUNCTION_CONFIGURATIONS_OFFICES_EDIT",
  FUNCTION_CONFIGURATIONS_OFFICES_ADD_NEW: "FUNCTION_CONFIGURATIONS_OFFICES_ADD_NEW",
  FUNCTION_CONFIGURATIONS_OPERATIONS_APP_VIEW: "FUNCTION_CONFIGURATIONS_OPERATIONS_APP_VIEW",
  FUNCTION_CONFIGURATIONS_OPERATIONS_APP_EDIT: "FUNCTION_CONFIGURATIONS_OPERATIONS_APP_EDIT",
  FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_VIEW: "FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_VIEW",
  FUNCTION_SETTINGS_PEOPLE_MEMBERS_VIEW: "FUNCTION_SETTINGS_PEOPLE_MEMBERS_VIEW",
  FUNCTION_SETTINGS_PEOPLE_GROUPS_VIEW: "FUNCTION_SETTINGS_PEOPLE_GROUPS_VIEW",
  FUNCTION_SETTINGS_PEOPLE_TEAMS_VIEW: "FUNCTION_SETTINGS_PEOPLE_TEAMS_VIEW",
  FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_EDIT: "FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_EDIT",
  FUNCTION_SETTINGS_PEOPLE_MEMBERS_EDIT: "FUNCTION_SETTINGS_PEOPLE_MEMBERS_EDIT",
  FUNCTION_SETTINGS_PEOPLE_GROUPS_EDIT: "FUNCTION_SETTINGS_PEOPLE_GROUPS_EDIT",
  FUNCTION_SETTINGS_PEOPLE_TEAMS_EDIT: "FUNCTION_SETTINGS_PEOPLE_TEAMS_EDIT",
  FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_ADD_NEW: "FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_ADD_NEW",
  FUNCTION_SETTINGS_PEOPLE_MEMBERS_ADD_NEW: "FUNCTION_SETTINGS_PEOPLE_MEMBERS_ADD_NEW",
  FUNCTION_SETTINGS_PEOPLE_MEMBERS_RESEND_WELCOME_EMAIL:
    "FUNCTION_SETTINGS_PEOPLE_MEMBERS_RESEND_WELCOME_EMAIL",
  FUNCTION_SETTINGS_PEOPLE_GROUPS_ADD_NEW: "FUNCTION_SETTINGS_PEOPLE_GROUPS_ADD_NEW",
  FUNCTION_SETTINGS_PEOPLE_TEAMS_ADD_NEW: "FUNCTION_SETTINGS_PEOPLE_TEAMS_ADD_NEW",
  FUNCTION_RESOURCES_SERVICES_VIEW: "FUNCTION_RESOURCES_SERVICES_VIEW",
  FUNCTION_RESOURCES_INVENTORY_VIEW: "FUNCTION_RESOURCES_INVENTORY_VIEW",
  FUNCTION_RESOURCES_SERVICES_EDIT: "FUNCTION_RESOURCES_SERVICES_EDIT",
  FUNCTION_RESOURCES_INVENTORY_EDIT: "FUNCTION_RESOURCES_INVENTORY_EDIT",
  FUNCTION_RESOURCES_SERVICES_ADD_NEW: "FUNCTION_RESOURCES_SERVICES_ADD_NEW",
  FUNCTION_RESOURCES_SERVICES_CHANGE_STATUS: "FUNCTION_RESOURCES_SERVICES_CHANGE_STATUS",
  FUNCTION_RESOURCES_INVENTORY_ADD_NEW: "FUNCTION_RESOURCES_INVENTORY_ADD_NEW",
  FUNCTION_RESOURCES_SUB_CONTRACTORS_ADD_NEW: "FUNCTION_RESOURCES_SUB_CONTRACTORS_ADD_NEW",
  FUNCTION_RESOURCES_SUB_CONTRACTORS_CHANGE_STATUS:
    "FUNCTION_RESOURCES_SUB_CONTRACTORS_CHANGE_STATUS",
  FUNCTION_RESOURCES_EQUIPMENTS_ADD_NEW: "FUNCTION_RESOURCES_EQUIPMENTS_ADD_NEW",
  FUNCTION_RESOURCES_EQUIPMENTS_CHANGE_STATUS: "FUNCTION_RESOURCES_EQUIPMENTS_CHANGE_STATUS",
  FUNCTION_RESOURCES_CHECKLIST_FORMS_ADD_NEW: "FUNCTION_RESOURCES_CHECKLIST_FORMS_ADD_NEW",
  FUNCTION_RESOURCES_CHECKLIST_FORMS_CHANGE_STATUS:
    "FUNCTION_RESOURCES_CHECKLIST_FORMS_CHANGE_STATUS",
  FUNCTION_RESOURCES_CHECKLIST_FORMS_EDIT: "FUNCTION_RESOURCES_CHECKLIST_FORMS_EDIT",
  FUNCTION_RESOURCES_EQUIPMENTS_EDIT: "FUNCTION_RESOURCES_EQUIPMENTS_EDIT",
  FUNCTION_RESOURCES_SUB_CONTRACTORS_EDIT: "FUNCTION_RESOURCES_SUB_CONTRACTORS_EDIT",
  FUNCTION_RESOURCES_SUB_CONTRACTORS_VIEW: "FUNCTION_RESOURCES_SUB_CONTRACTORS_VIEW",
  FUNCTION_RESOURCES_EQUIPMENTS_VIEW: "FUNCTION_RESOURCES_EQUIPMENTS_VIEW",
  FUNCTION_RESOURCES_CHECKLIST_FORMS_VIEW: "FUNCTION_RESOURCES_CHECKLIST_FORMS_VIEW",
  FUNCTION_RESOURCES_INSTRUCTIONS_VIEW: "FUNCTION_RESOURCES_INSTRUCTIONS_VIEW",
  FUNCTION_RESOURCES_MATERIALS_VIEW: "FUNCTION_RESOURCES_MATERIALS_VIEW",
  FUNCTION_RESOURCES_ACCESS_VIEW: "FUNCTION_RESOURCES_ACCESS_VIEW",
  FUNCTION_RESOURCES_ACCESS_EDIT: "FUNCTION_RESOURCES_ACCESS_EDIT",
  FUNCTION_RESOURCES_MATERIALS_EDIT: "FUNCTION_RESOURCES_MATERIALS_EDIT",
  FUNCTION_RESOURCES_INSTRUCTIONS_EDIT: "FUNCTION_RESOURCES_INSTRUCTIONS_EDIT",
  FUNCTION_RESOURCES_INSTRUCTIONS_ADD_NEW: "FUNCTION_RESOURCES_INSTRUCTIONS_ADD_NEW",
  FUNCTION_RESOURCES_INSTRUCTIONS_CHANGE_STATUS: "FUNCTION_RESOURCES_INSTRUCTIONS_CHANGE_STATUS",
  FUNCTION_RESOURCES_MATERIALS_ADD_NEW: "FUNCTION_RESOURCES_MATERIALS_ADD_NEW",
  FUNCTION_RESOURCES_MATERIALS_CHANGE_STATUS: "FUNCTION_RESOURCES_MATERIALS_CHANGE_STATUS",
  FUNCTION_RESOURCES_ACCESS_ADD_NEW: "FUNCTION_RESOURCES_ACCESS_ADD_NEW",
  FUNCTION_RESOURCES_ACCESS_CHANGE_STATUS: "FUNCTION_RESOURCES_ACCESS_CHANGE_STATUS",
  FUNCTION_TEMPLATES_SMS_VIEW: "FUNCTION_TEMPLATES_SMS_VIEW",
  FUNCTION_TEMPLATES_SMS_DUPLICATE: "FUNCTION_TEMPLATES_SMS_DUPLICATE",
  FUNCTION_TEMPLATES_EMAIL_VIEW: "FUNCTION_TEMPLATES_EMAIL_VIEW",
  FUNCTION_TEMPLATES_EMAIL_DUPLICATE: "FUNCTION_TEMPLATES_EMAIL_DUPLICATE",
  FUNCTION_TEMPLATES_SMS_EDIT: "FUNCTION_TEMPLATES_SMS_EDIT",
  FUNCTION_TEMPLATES_EMAIL_EDIT: "FUNCTION_TEMPLATES_EMAIL_EDIT",
  FUNCTION_TEMPLATES_SMS_ADD_NEW: "FUNCTION_TEMPLATES_SMS_ADD_NEW",
  FUNCTION_TEMPLATES_EMAIL_ADD_NEW: "FUNCTION_TEMPLATES_EMAIL_ADD_NEW",
};

export {
  operationsPermissions,
  warehousingPermissions,
  salesPermissions,
  managementPermissions,
  hasPermission,
  checkResourceCan,
  isShowAction,
  FN_NAMES,
};
